import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, Upload, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { image_url: "", eyebrow: "", title: "", description: "" };

/**
 * Editor for the home page hero highlight block (image + eyebrow + title +
 * description). Open to admin AND assistant — backed by /api/site/home-hero.
 */
export default function HomeHeroEditor() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get("/site/home-hero");
      setData({ ...EMPTY, ...r.data });
    } catch {
      toast.error("Impossible de charger le bloc d'accueil");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.image_url) return toast.error("Une image est requise");
    if (!data.title.trim()) return toast.error("Le titre est obligatoire");
    setSaving(true);
    try {
      await api.put("/site/home-hero", data);
      toast.success("Bloc d'accueil mis à jour");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/site/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || "Upload échoué");
      const url = json.url.startsWith("http") ? json.url : `${BACKEND_URL}${json.url}`;
      set("image_url", url);
      toast.success("Image téléchargée");
    } catch (err) {
      toast.error(err.message || "Erreur upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div data-testid="home-hero-editor-loading" className="text-sm text-[#4B634B] py-12 text-center">
        Chargement…
      </div>
    );
  }

  return (
    <div className="grid gap-8 md:grid-cols-2" data-testid="home-hero-editor">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Image (URL ou upload)</Label>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Input
              data-testid="home-hero-image-url"
              value={data.image_url}
              onChange={(e) => set("image_url", e.target.value)}
              placeholder="https://… ou /api/files/…"
              required
            />
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={onFile}
              className="hidden"
              data-testid="home-hero-file-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="home-hero-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Uploader
            </Button>
          </div>
          <p className="text-xs text-[#4B634B] mt-1">PNG / JPG / WebP — max 5 Mo. Format recommandé : 1200×1500 px (ratio portrait).</p>
        </div>

        <div>
          <Label>Mention en haut (eyebrow)</Label>
          <Input
            data-testid="home-hero-eyebrow"
            value={data.eyebrow}
            onChange={(e) => set("eyebrow", e.target.value)}
            placeholder="ex : Édition limitée"
            maxLength={60}
          />
        </div>

        <div>
          <Label>Titre</Label>
          <Input
            data-testid="home-hero-title"
            value={data.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="ex : Berry Boost"
            required
            maxLength={80}
          />
        </div>

        <div>
          <Label>Description courte</Label>
          <Textarea
            data-testid="home-hero-description"
            value={data.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="ex : Énergie naturelle et antioxydants — quantité limitée."
            rows={3}
            maxLength={240}
          />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button
            type="submit"
            className="bg-[#7AC143] hover:bg-[#67BC2A] text-white"
            disabled={saving}
            data-testid="home-hero-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Enregistrer
          </Button>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[#4B634B] hover:text-[#1A2E1A] inline-flex items-center gap-1"
            data-testid="home-hero-preview-link"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Voir sur la home
          </a>
        </div>
      </form>

      {/* Live preview */}
      <div>
        <Label className="block mb-3">Aperçu</Label>
        <div className="relative h-[420px] bg-[#F2F5F0] border border-[#E5EAE5] rounded-3xl overflow-hidden" data-testid="home-hero-preview">
          {data.image_url ? (
            <img
              src={data.image_url}
              alt={data.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-[#9CB39C] text-sm">
              Aucune image
            </div>
          )}
          <div className="absolute -bottom-6 -left-6 bg-white border border-[#E5EAE5] rounded-2xl p-5 shadow-lg max-w-[240px]">
            {data.eyebrow && (
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#7AC143]">{data.eyebrow}</div>
            )}
            <div className="font-black text-lg mt-1">{data.title || "Titre"}</div>
            {data.description && (
              <div className="text-sm text-[#4B634B] mt-1">{data.description}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
